import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Link as LinkTo } from "react-router";
import { ObrasTopBar } from "../../components/obras_top_bar";
import { ObrasTopHeader } from "../../components/obras_top_header";
import { useObraItensChecklistAreaVerificacaoViewModel } from "../../viewModels/obra_itens_checklist_area_verificacao_view_model";
import { InformacoesGeraisPanel } from "./components/informacoes_gerais_panel";
import { isValidId } from "../../../../../common/utils/utils";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendTagIcon } from "../../../../../common/components/legend_tag_icon";
import { LegendTagItemComponent } from "../../../../../common/components/legend_tag_item_component";
import { useMemo } from "react";
import { GenerateInspecaoObraItensColumnsDefinition } from "./columns";
import { OcorrenciaModal } from "./components/ocorrencia_modal";
import { ExecutadoPorModal } from "./components/executador_por_modal";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { GenerateExecutadoPorColumnsDefinition } from "./columns/executador_por";
import { ExecutadoPor } from "../../../domain/models/executador_por";
import { InspecaoObraOcorrencia } from "../../../domain/models/inspecao_obra_ocorrencia";
import { GenerateOcorrenciaColumnsDefinition } from "./columns/inspecao_obra_ocorrencia";
import { InspecaoObraItemForm } from "../../schemas/ObraItensChecklistArea_verificacao/inspecao_obra_verificacao";
import { DefaultFormDownloadInput } from "../../../../../common/components/default_form_download_input";
import { ObrasReportModals } from "../../components/obras_report_modals";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { CustomStyledDataGridItens } from "./components/custom_styled_data_grid_itens";

import AlertError from "../../../../../common/components/alert_error";
import BottomBar from "../../../../../common/components/bottom_bar";
import PrimaryButton from "../../../../../common/components/primary_button";
import FormContainer from "../../../../../common/components/form_container";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import DefaultFormInput from "../../../../../common/components/default_form_input";

/**
 * ObraItensChecklistAreaVerificacao
 */

export const ObraItensChecklistAreaVerificacao = () => {
  const viewModel = useObraItensChecklistAreaVerificacaoViewModel();

  const inspecaoObraItensColumnsDefinition = useMemo(() => {
    return GenerateInspecaoObraItensColumnsDefinition({
      atributoObras: viewModel.atributoObras,
      statusChecklistServico: viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status : null,
      lockInspecaoObraItens: viewModel.lockInspecaoObraItens,
      onActionOcorrencia: viewModel.handleOcorrenciaModalOpenState,
      onActionInspecao1Change: viewModel.handleInspecao1Change,
      onActionDataInspecao1Change: viewModel.handleDataInspecao1Change,
      onActionInspecao2Change: viewModel.handleInspecao2Change,
      onActionDataInspecao2Change: viewModel.handleDataInspecao2Change,
      onActionLimpar: viewModel.handleClearInspecaoObraItem,
    });
  }, [
    viewModel.atributoObras,
    viewModel.obraChecklistServicoHeader,
    viewModel.lockInspecaoObraItens,
    viewModel.handleOcorrenciaModalOpenState,
    viewModel.handleInspecao1Change,
    viewModel.handleDataInspecao1Change,
    viewModel.handleInspecao2Change,
    viewModel.handleDataInspecao2Change,
    viewModel.handleClearInspecaoObraItem,
  ]);

  const executadoPorColumnsDefinition = useMemo(() => {
    return GenerateExecutadoPorColumnsDefinition({
      lockFuncionarioAprovado: viewModel.lockFuncionarioAprovado,
      statusChecklistServico: viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status : null,
      onActionDelete: viewModel.handleDeleteExecutadoPorModalOpenState,
    });
  }, [
    viewModel.lockFuncionarioAprovado,
    viewModel.obraChecklistServicoHeader,
    viewModel.handleDeleteExecutadoPorModalOpenState,
  ]);

  const ocorrenciaColumnsDefinition = useMemo(() => {
    return GenerateOcorrenciaColumnsDefinition({
      lockFuncionarioAprovado: viewModel.lockFuncionarioAprovado,
      statusChecklistServico: viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status : null,
      onActionEditar: viewModel.handleOcorrenciaModalOpenState,
      onActionDeletar: viewModel.handleDeleteOcorrenciaModalOpenState,
    });
  }, [
    viewModel.lockFuncionarioAprovado,
    viewModel.obraChecklistServicoHeader,
    viewModel.handleOcorrenciaModalOpenState,
    viewModel.handleDeleteOcorrenciaModalOpenState,
  ]);

  return (
    <>
      <form onSubmit={viewModel.handleSubmit}>

        {/**
          * ObrasTopBar
          */}

        <ObrasTopBar
          obrasReportState={viewModel.obrasReportState}
          state={viewModel.obrasTopHeaderState}
          selectedObra={viewModel.selectedObraHeaderInfo ?? null}
          onBack={viewModel.handleBack}>
        </ObrasTopBar>

        <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
          <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
            <Link underline="hover" color="inherit">
              Obras
            </Link>
            <Link underline="hover" color="inherit" component={'span'}>
              <LinkTo to='/Obras_lista'>Lista de Obras</LinkTo>
            </Link>
            <Link underline="hover" color="inherit" component={'span'}>
              <LinkTo to={`/Obras_cadastroarea?id=${viewModel.idObra}`}>Checklist de Área</LinkTo>
            </Link>
            <Link underline="hover" color="inherit" component={'span'}>
              <LinkTo to={`/Obras_checklistarea?id=${viewModel.idObra}&idChecklistArea=${viewModel.idObraAreaChecklist}`}>Checklist de Serviço</LinkTo>
            </Link>
            <Link underline="hover" color="inherit" component={'span'}>
              <LinkTo to={`/ObraItensChecklistArea_cadastro?id=${viewModel.idObra}&idChecklistArea=${viewModel.idObraAreaChecklist}&idChecklistServico=${viewModel.idObraChecklistServico}`}>Itens do Checklist</LinkTo>
            </Link>
            <Typography className="text-primary-green">
              Ficha de Verificação de Serviços
            </Typography>
          </Breadcrumbs>

          {/**
          * ObrasTopHeader
          */}

          <ObrasTopHeader
            title="Ficha de Verificação de Serviços"
            selectedObra={viewModel.selectedObraHeaderInfo ?? null}
            additionalHeader={
              viewModel.obraChecklistServicoHeader && (
                <div className="flex flex-row gap-8">
                  <div className="flex flex-row text-black dark:text-white items-center">
                    <span className="font-extrabold text-xl mr-2">Área: </span>
                    <span className="mx-1 font-base text-base">{viewModel.obraChecklistServicoHeader.descricaoArea}</span>
                  </div>
                  <div className="flex flex-row text-black dark:text-white items-center">
                    <span className="font-extrabold text-xl mr-2">Checklist: </span>
                    <span className="mx-1 font-base text-base">{viewModel.obraChecklistServicoHeader.descricaoServico}</span>
                  </div>
                  <div className="flex flex-row text-black dark:text-white items-center">
                    <span className="font-extrabold text-base mr-2">Revisão: </span>
                    <span className="mx-1 font-base text-base">{viewModel.obraChecklistServicoHeader.revisao}</span>
                  </div>
                </div>
              )
            }
            onReload={viewModel.handleReload}
          />

          {
            viewModel.errorMessage !== undefined
              ? (
                <div className="w-full">
                  <AlertError
                    messageErro={viewModel.errorMessage}
                    onTryAgain={viewModel.handleReload}
                  />
                </div>
              ) : (
                <>
                  {
                    (viewModel.estatisticasChecklistServico && viewModel.obraChecklistServicoCampos && viewModel.inspecaoObra) &&
                    <InformacoesGeraisPanel
                      statusChecklistServico={viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status : null}
                      lockFuncionarioInspecionado={viewModel.lockFuncionarioInspecionado}
                      lockFuncionarioAprovado={viewModel.lockFuncionarioAprovado}
                      selectedObraHeaderInfo={viewModel.selectedObraHeaderInfo ?? null}
                      obraChecklistServicoCampos={viewModel.obraChecklistServicoCampos}
                      inspecaoObra={viewModel.inspecaoObra}
                      funcionariosDropDown={viewModel.funcionariosDropdown}
                      hookForm={viewModel.hookForm}
                    />
                  }
                  <Stack spacing={0}>
                    <div className="w-full flex flex-row justify-between items-end">
                      <div className="flex flex-col flex-wrap">
                        <LegendComponent>
                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='NA' bgColor="#89c4f4" />}
                            label='Não Se Aplica'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='A' bgColor="#45b6af" />}
                            label='Aprovado'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='R' bgColor="#f8cb00" />}
                            label='Retrabalho'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='X' bgColor="#f3565d" />}
                            label='Reprovado'
                          />

                          <LegendTagItemComponent
                            icon={<LegendTagIcon title='RA' bgColor="#45b6af" />}
                            label='Retrabalho Aprovado'
                          />

                        </LegendComponent>
                      </div>
                    </div>

                    <CustomStyledDataGridItens
                      tableName="obra_itens_checklist_area_verificacao_inspecoes_items"
                      disableColumnMenu={false}
                      getRowId={(row: InspecaoObraItemForm) => row.idInspecaoObraItem}
                      textSize="muitoPequeno"
                      columns={inspecaoObraItensColumnsDefinition}
                      loading={viewModel.loading}
                      rows={viewModel.inspecoesObraItens}
                      rowCount={viewModel.inspecoesObraItens.length}
                      getRowHeight={() => 'auto'}
                      disableRowSelectionOnClick={true}
                      disableColumnSorting={false}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode="server"
                      paginationModel={{
                        page: viewModel.pagination.inspecaoObraItens.page,
                        pageSize: viewModel.pagination.inspecaoObraItens.rowsPerPage,
                      }}
                      treeData
                      onFillCurrentDateChange={viewModel.handleFillCurrentDateChange}
                      getTreeDataPath={(row) => row.path}
                      groupingColDef={{
                        headerName: 'Descrição',
                        flex: 1,
                        minWidth: 150,
                        hideDescendantCount: true,
                        valueFormatter: (value: any) =>
                          typeof value === 'string' ? value.replace(/^\(\d+\)\s*/, '') : value,
                      }}
                    />

                    <TablePagination
                      component="div"
                      count={viewModel.pagination.inspecaoObraItens.totalItems}
                      page={viewModel.pagination.inspecaoObraItens.page - 1}
                      onPageChange={(_, p) => viewModel.handleInspecaoObraItensPageChange(p)}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      rowsPerPage={viewModel.pagination.inspecaoObraItens.rowsPerPage}
                      onRowsPerPageChange={(e) => viewModel.handleInspecaoObraItensRowsPerPageChange(parseInt(e.target.value))}
                    />

                  </Stack>

                  <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
                    <div className="w-full flex flex-col gap-2 mb-2">
                      <div className="flex flex-row items-center justify-between">
                        <h1 className="font-bold text-black dark:text-white">Ocorrências</h1>
                        <div>
                          {
                            (viewModel.obraChecklistServicoHeader && viewModel.obraChecklistServicoHeader.status !== 1 && !viewModel.lockFuncionarioAprovado) &&
                            <PrimaryButton
                              buttonColor="primary-green"
                              startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                              onMouseDown={e => {
                                viewModel.handleOcorrenciaModalOpenState(null, null, true);
                                e.preventDefault();
                              }}>
                              Novo Item
                            </PrimaryButton>
                          }
                        </div>
                      </div>
                    </div>

                    <div className="w-full flex flex-col">
                      <StyledDataGrid
                        tableName="obra_itens_checklist_area_verificacao_ocorrencias"
                        disableColumnMenu={false}
                        autoHeight={true}
                        textSize="pequeno"
                        getRowHeight={() => 'auto'}
                        getRowId={(row: InspecaoObraOcorrencia) => row.idInformacoesInspecaoObra}
                        columns={ocorrenciaColumnsDefinition}
                        loading={viewModel.loading}
                        rows={viewModel.inspecaoObraOcorrencias}
                        rowCount={viewModel.inspecaoObraOcorrencias.length}
                        disableRowSelectionOnClick={true}
                        disableColumnSorting={false}
                        pageSizeOptions={[10, 25, 50, 100]}
                        paginationMode="server"
                        paginationModel={{
                          page: viewModel.pagination.ocorrencias.page,
                          pageSize: viewModel.pagination.ocorrencias.rowsPerPage,
                        }}
                      />

                      <TablePagination
                        component="div"
                        count={viewModel.pagination.ocorrencias.totalItems}
                        page={viewModel.pagination.ocorrencias.page - 1}
                        onPageChange={(_, p) => viewModel.handleInspecaoObraOcorrenciaPageChange(p)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        rowsPerPage={viewModel.pagination.ocorrencias.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleInspecaoObraOcorrenciaRowsPerPageChange(parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
                    <div className="w-full flex flex-col">
                      <FormContainer title="Observações" arrangement="fullRow" border={false}>
                        <>
                          <DefaultFormInput
                            name='observacao'
                            multiline={true}
                            rows={10}
                            readOnly={viewModel.lockFuncionarioAprovado
                              ? true
                              : viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status === 1 : false}
                            reactHookForm={viewModel.hookForm}
                          />
                        </>
                      </FormContainer>

                      <FormContainer title="Arquivos" arrangement="fullRow" border={false}>
                        <div className="w-full flex flex-col gap-2">
                          <div className="w-full flex flex-row gap-2">
                            <div className="w-[35rem]">
                              <DefaultFormDownloadInput
                                name='arquivo1'
                                nameLimparFlag='limparArquivo1'
                                saving={viewModel.saving}
                                filename={viewModel.inspecaoObra?.nomeArquivo1 ?? null}
                                readOnly={viewModel.lockFuncionarioAprovado
                                  ? true
                                  : viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status === 1 : false}
                                reactHookForm={viewModel.hookForm}
                                onDownloadFile={filename => viewModel.handleDownloadFile(filename, 1)}
                                onDeleteFile={() => viewModel.handleDeleteFile(1)}
                              />
                            </div>

                            <DefaultFormInput
                              name='descricaoArquivo1'
                              label='Descrição arquivo 1'
                              reactHookForm={viewModel.hookForm}
                            />
                          </div>

                          <div className="w-full flex flex-row gap-2">
                            <div className="w-[35rem]">
                              <DefaultFormDownloadInput
                                name='arquivo2'
                                nameLimparFlag='limparArquivo2'
                                saving={viewModel.saving}
                                filename={viewModel.inspecaoObra?.nomeArquivo2 ?? null}
                                readOnly={viewModel.lockFuncionarioAprovado
                                  ? true
                                  : viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status === 1 : false}
                                reactHookForm={viewModel.hookForm}
                                onDownloadFile={filename => viewModel.handleDownloadFile(filename, 2)}
                                onDeleteFile={() => viewModel.handleDeleteFile(2)}
                              />
                            </div>

                            <DefaultFormInput
                              name='descricaoArquivo2'
                              label='Descrição arquivo 2'
                              reactHookForm={viewModel.hookForm}
                            />
                          </div>

                          <div className="w-full flex flex-row gap-2">
                            <div className="w-[35rem]">
                              <DefaultFormDownloadInput
                                name='arquivo3'
                                nameLimparFlag='limparArquivo3'
                                saving={viewModel.saving}
                                filename={viewModel.inspecaoObra?.nomeArquivo3 ?? null}
                                readOnly={viewModel.lockFuncionarioAprovado
                                  ? true
                                  : viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status === 1 : false}
                                reactHookForm={viewModel.hookForm}
                                onDownloadFile={filename => viewModel.handleDownloadFile(filename, 3)}
                                onDeleteFile={() => viewModel.handleDeleteFile(3)}
                              />
                            </div>

                            <DefaultFormInput
                              name='descricaoArquivo3'
                              label='Descrição arquivo 3'
                              reactHookForm={viewModel.hookForm}
                            />
                          </div>

                          <div className="w-full flex flex-row gap-2">
                            <div className="w-[35rem]">
                              <DefaultFormDownloadInput
                                name='arquivo4'
                                nameLimparFlag='limparArquivo4'
                                saving={viewModel.saving}
                                filename={viewModel.inspecaoObra?.nomeArquivo4 ?? null}
                                readOnly={viewModel.lockFuncionarioAprovado
                                  ? true
                                  : viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status === 1 : false}
                                reactHookForm={viewModel.hookForm}
                                onDownloadFile={filename => viewModel.handleDownloadFile(filename, 4)}
                                onDeleteFile={() => viewModel.handleDeleteFile(4)}
                              />
                            </div>

                            <DefaultFormInput
                              name='descricaoArquivo4'
                              label='Descrição arquivo 4'
                              reactHookForm={viewModel.hookForm}
                            />
                          </div>

                          <div className="w-full flex flex-row gap-2">
                            <div className="w-[35rem]">
                              <DefaultFormDownloadInput
                                name='arquivo5'
                                nameLimparFlag='limparArquivo5'
                                saving={viewModel.saving}
                                filename={viewModel.inspecaoObra?.nomeArquivo5 ?? null}
                                readOnly={viewModel.lockFuncionarioAprovado
                                  ? true
                                  : viewModel.obraChecklistServicoHeader ? viewModel.obraChecklistServicoHeader.status === 1 : false}
                                reactHookForm={viewModel.hookForm}
                                onDownloadFile={filename => viewModel.handleDownloadFile(filename, 5)}
                                onDeleteFile={() => viewModel.handleDeleteFile(5)}
                              />
                            </div>

                            <DefaultFormInput
                              name='descricaoArquivo5'
                              label='Descrição arquivo 5'
                              reactHookForm={viewModel.hookForm}
                            />
                          </div>
                        </div>
                      </FormContainer>
                    </div>
                  </div>

                  <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
                    <div className="w-full flex flex-col gap-2">
                      <div className="w-full flex flex-col gap-2 mb-2">
                        <div className="flex flex-row items-center justify-between">
                          <h1 className="font-bold text-black dark:text-white">Serviço Executado Por</h1>
                          <div>
                            {
                              (viewModel.obraChecklistServicoHeader && viewModel.obraChecklistServicoHeader.status !== 1 && !viewModel.lockFuncionarioAprovado) &&
                              <PrimaryButton
                                buttonColor="primary-green"
                                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                                onMouseDown={e => {
                                  viewModel.handleExecutadoPorModalOpenState(true);
                                  e.preventDefault();
                                }}>
                                Novo Item
                              </PrimaryButton>
                            }
                          </div>
                        </div>
                      </div>

                      <div className="w-full flex flex-col">
                        <StyledDataGrid
                          tableName="obra_itens_checklist_area_verificacao_executador_por"
                          disableColumnMenu={false}
                          autoHeight={true}
                          getRowId={(row: ExecutadoPor) => row.idExecutadoPor}
                          columns={executadoPorColumnsDefinition}
                          loading={viewModel.loading}
                          rows={viewModel.executadoPor}
                          rowCount={viewModel.executadoPor.length}
                          getRowHeight={() => 'auto'}
                          disableRowSelectionOnClick={true}
                          disableColumnSorting={false}
                          pageSizeOptions={[10, 25, 50, 100]}
                          paginationMode="server"
                          paginationModel={{
                            page: viewModel.pagination.executadoPor.page,
                            pageSize: viewModel.pagination.executadoPor.rowsPerPage,
                          }}
                        />

                        <TablePagination
                          component="div"
                          count={viewModel.pagination.executadoPor.totalItems}
                          page={viewModel.pagination.executadoPor.page - 1}
                          onPageChange={(_, p) => viewModel.handleExecutadoPorPageChange(p)}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          rowsPerPage={viewModel.pagination.executadoPor.rowsPerPage}
                          onRowsPerPageChange={(e) => viewModel.handleExecutadoPorRowsPerPageChange(parseInt(e.target.value))}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )
          }
        </div >

        {/**
          * BottomBar
          */}

        <BottomBar>
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div>
              {
                isValidId(viewModel.idInspecaoObra) &&
                <>
                  {
                    (viewModel.atributoObras && viewModel.atributoObras.exibirDataChecklist) &&
                    <PrimaryButton
                      buttonColor="yellow"
                      onClick={() => viewModel.handleOpenChangeHistory(null, null)}>
                      <span className="text-xs">
                        Historico de alterações
                      </span>
                    </PrimaryButton>
                  }
                </>
              }
            </div>

            <div className="flex flex-row gap-4">
              {
                (viewModel.obraChecklistServicoHeader && viewModel.obraChecklistServicoHeader.status === 0) &&
                <div className="w-[8rem]">
                  {
                    viewModel.saving
                      ? (
                        <PrimaryButton>
                          Salvando...
                        </PrimaryButton>
                      ) : (
                        <PrimaryButton
                          type="submit"
                          startIcon={<DynamicIcon nameIcon="CheckRounded" size={25} className="text-white" />}>
                          Salvar
                        </PrimaryButton>
                      )
                  }
                </div>
              }
            </div>
          </div>
        </BottomBar>
      </form>

      {/**
        * Modals
        */}

      <OcorrenciaModal
        open={viewModel.ocorrenciaModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        fillOcorrenciaData={viewModel.fillOcorrenciaData}
        inspecoesObraItens={viewModel.inspecoesObraItensBase}
        selectedInspecaoObraItem={viewModel.selectedInspecaoObraItem}
        selectedOcorrencia={viewModel.selectedOcorrencia}
        onClose={() => viewModel.handleOcorrenciaModalOpenState(null, null, false)}
        onSubmit={viewModel.handleOcorrenciaSubmit}
      />

      <ExecutadoPorModal
        open={viewModel.executadoPorModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        fornecedoresDropDown={viewModel.fornecedoresDropDown}
        funcionariosDropDown={viewModel.filteredFuncionariosDropdown}
        funcionariosTercDropDown={viewModel.funcionariosTercDropDown}
        onClose={() => viewModel.handleExecutadoPorModalOpenState(false)}
        onSubmit={viewModel.handleExecutadoPorSubmit}
      />

      <DeleteDialog
        open={viewModel.deleteOcorrenciaModal}
        onClose={() => viewModel.handleDeleteOcorrenciaModalOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleDeleteOcorrenciaSubmit}
      />

      <DeleteDialog
        open={viewModel.deleteExecutadoPorModal}
        onClose={() => viewModel.handleDeleteExecutadoPorModalOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleDeleteExecutadoPorSubmit}
      />

      {/**
        * Relatorios obras.
        */}

      <ObrasReportModals
        state={viewModel.obrasReportState}
      />

      {/**
        * Modal historico de alterações.
        */}

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory}
      />

    </>
  );
}